/* eslint-disable camelcase */
import React, { useState, useRef } from 'react';

import { useRouter } from 'next/router';
import { FaChevronDown } from 'react-icons/fa';
import styled from 'styled-components';

import { useMembership } from '~/techstyle-shared/react-accounts';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import useBuilderConstructorGridInputs from '../hooks/useBuilderConstructorGridInputs';
import useProductGridData from '../hooks/useProductGridData';
import {
  getSanitizedSortOptions,
  getSortOptionRbId,
  isSortOptionSelected,
  updateSortInUrlParams,
} from '../utils/sortOptions';

const DynamicFormattedMessage = FormattedMessage;

const GridSortDropdownWrapper = styled.div`
  position: relative;
  z-index: 5;
  width: auto;
`;

const GridSortDropdownContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: 16px;
  width: 100%;
`;

const GridSortDropdownButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.375rem;
  height: 3rem;
  width: 100%;
  background-color: white;
  border-radius: 0.125rem;
  border: none;
  cursor: pointer;
  font-family: inherit;
  padding: 0px;
  ${({ theme }) => theme.gridFilterStyles.gridLabelStyle};
`;

const GridSortDropdownButtonText = styled.div`
  display: flex;
`;

const GridSortDropdownList = styled.div`
  position: absolute;
  z-index: 50;
  width: 100vw;
  padding: 0.5rem;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.palette.shadow15};
  border-radius: 0.125rem;
  ${({ theme }) => theme.gridFilterStyles.listingFilterStyle};
`;

const GridSortDropdownOption = styled.button`
  width: 100%;
  max-width: 100vw;
  padding: 1rem 0.5rem;
  font-size: 1rem;
  text-align: left;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gridSortDropdownItemTextColorDefault};
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.gridSortDropdownItemBackgroundColorHover};
    color: ${({ theme }) => theme.colors.gridSortDropdownItemTextColorHover};
    outline: none;
  }
`;

const ChevronIcon = styled(FaChevronDown)`
  font-size: 8px;
  color: black;

  &.open {
    transform: rotate(180deg);
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 100%;
  width: 100vw;
  left: 0;
  right: 0;
  height: calc(
    100vh - 3rem
  ); /* Adjust this value to match the height of the dropdown button */
  background: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 40; /* Ensure overlay stays below the dropdown */
`;

const SelectedSortText = styled.div`
  color: ${({ theme }) => theme.colors.gridSortDropdownSortLabelTextColor};
  font-family: inherit;
  font-weight: 500;
  padding-left: 2px;
`;

const MobileGridSortDropdown = () => {
  const router = useRouter();
  const { isVip } = useMembership();
  const { sortOptions: builderSortOptions } = useBuilderConstructorGridInputs();
  const { cioSortOptions } = useProductGridData();
  const [isOpen, setIsOpen] = useState(false);

  const buttonRef = useRef();

  if (!cioSortOptions) {
    return null;
  }

  const sanitizedSortOptions = getSanitizedSortOptions({
    builderSortOptions,
    cioSortOptions,
    isVip,
    excludeSelectedOption: false,
  });

  const handleSortOptionClick = (sortBy) => {
    updateSortInUrlParams({ router, sort: sortBy });
    setIsOpen(false);
  };

  const selectedSortOption = cioSortOptions.find(({ status }) =>
    isSortOptionSelected({ status })
  );

  const selectedSortOptionDisplayName = selectedSortOption?.display_name;

  return (
    <GridSortDropdownWrapper>
      <GridSortDropdownContainer>
        <GridSortDropdownButton
          ref={buttonRef}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
          data-autotag="sort-dropdown"
        >
          <GridSortDropdownButtonText>
            <FormattedMessage
              id="site_product_grid.sort_label"
              defaultMessage="Sort"
            />
            {selectedSortOptionDisplayName && (
              <>
                <span>: </span>
                <SelectedSortText>
                  <DynamicFormattedMessage
                    id={`site_product_filter.${getSortOptionRbId(
                      `sort_${selectedSortOption?.sort_by}`
                    )}`}
                    defaultMessage={selectedSortOptionDisplayName}
                  />
                </SelectedSortText>
              </>
            )}
          </GridSortDropdownButtonText>
          <ChevronIcon className={isOpen ? 'open' : ''} aria-hidden="true" />
        </GridSortDropdownButton>
      </GridSortDropdownContainer>

      {isOpen && sanitizedSortOptions?.length > 0 && (
        <>
          <GridSortDropdownList
            role="listbox"
            tabIndex={-1}
            aria-label="Sort options"
          >
            {sanitizedSortOptions.map(({ display_name, sort_by }) => (
              <GridSortDropdownOption
                key={sort_by}
                role="option"
                onClick={() => handleSortOptionClick(sort_by)}
                data-autotag={`sort-by-${sort_by}`}
              >
                <DynamicFormattedMessage
                  id={`site_product_filter.${getSortOptionRbId(
                    `sort_${sort_by}`
                  )}`}
                  defaultMessage={display_name}
                />
              </GridSortDropdownOption>
            ))}
          </GridSortDropdownList>
          <Overlay onClick={() => setIsOpen(false)} />
        </>
      )}
    </GridSortDropdownWrapper>
  );
};

export default MobileGridSortDropdown;
